import { useMemo } from 'react';
import { Link } from 'react-router-dom';

type LegalPageTemplateProps = {
  content: string;
  title: string;
  effectiveDate: string;
  lastUpdated: string;
};

function slugify(value: string) {
  return value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

type Block =
  | { kind: 'paragraph'; text: string }
  | { kind: 'list'; items: string[] }
  | { kind: 'section'; text: string; id: string }
  | { kind: 'subsection'; text: string };

function isNumberedHeading(line: string) {
  return /^\d+\.\s+/.test(line) || /^\d+\.\d+(\.\d+)*\s+/.test(line);
}

function isSubsectionHeading(line: string) {
  if (line.length < 6 || line.length > 64) return false;
  if (line.includes(':')) return false;
  if (line === line.toUpperCase()) return false;
  if (/[.!?]$/.test(line)) return false;
  if (!/[A-Za-z]/.test(line) || !/\s/.test(line)) return false;
  return true;
}

function parseLegalContent(content: string): Block[] {
  const lines = content
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean);

  const parsed: Block[] = [];

  for (const line of lines) {
    if (isNumberedHeading(line)) {
      parsed.push({ kind: 'section', text: line, id: slugify(line) });
      continue;
    }

    if (isSubsectionHeading(line)) {
      parsed.push({ kind: 'subsection', text: line });
      continue;
    }

    if (line.startsWith('•')) {
      const item = line.replace(/^•\s*/, '');
      const prev = parsed[parsed.length - 1];
      if (prev?.kind === 'list') {
        prev.items.push(item);
      } else {
        parsed.push({ kind: 'list', items: [item] });
      }
      continue;
    }

    parsed.push({ kind: 'paragraph', text: line });
  }

  return parsed;
}

export function LegalPageTemplate({
  content,
  title,
  effectiveDate,
  lastUpdated,
}: LegalPageTemplateProps) {
  const blocks = useMemo(() => parseLegalContent(content), [content]);

  const sections = useMemo(
    () => blocks.filter((block): block is Extract<Block, { kind: 'section' }> => block.kind === 'section'),
    [blocks],
  );

  return (
    <div className="min-h-screen bg-surface-lowest text-on-surface">
      <div className="pointer-events-none fixed inset-0 opacity-[0.05] bg-[radial-gradient(ellipse_at_20%_20%,rgba(255,181,161,0.22),transparent_55%),radial-gradient(ellipse_at_80%_80%,rgba(204,51,0,0.22),transparent_55%)]" />

      <header className="sticky top-0 z-30 border-b border-white/10 bg-[rgba(14,14,14,0.82)] [backdrop-filter:blur(14px)]">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-6 py-4 sm:px-12">
          <Link to="/" className="font-mono text-[10px] tracking-[0.18em] uppercase text-on-surface-variant hover:text-primary">
            ← Back to Home
          </Link>
          <span className="font-mono text-[10px] tracking-[0.18em] uppercase text-on-surface-muted">{title}</span>
        </div>
      </header>

      <main className="relative mx-auto grid max-w-7xl gap-10 px-6 py-10 sm:px-12 lg:grid-cols-[minmax(0,1fr)_300px]">
        <article className="rounded-xl border border-white/10 bg-surface-low/60 p-6 sm:p-10">
          <h1 className="font-display text-[clamp(34px,4vw,54px)] leading-[0.95] tracking-[-0.02em] text-[#f2efea]">{title}</h1>
          <p className="mt-4 font-mono text-[11px] tracking-[0.14em] uppercase text-on-surface-variant">
            Effective Date: {effectiveDate} | Last Updated: {lastUpdated}
          </p>

          <div className="mt-8 space-y-4 text-[15px] leading-[1.8] text-on-surface-variant sm:text-[16px]">
            {blocks.map((block, idx) => {
              if (block.kind === 'section') {
                return (
                  <h2 key={`${block.id}-${idx}`} id={block.id} className="scroll-mt-24 pt-4 font-display text-[24px] leading-tight tracking-[-0.01em] text-on-surface">
                    {block.text}
                  </h2>
                );
              }

              if (block.kind === 'list') {
                return (
                  <ul key={`ul-${idx}`} className="list-disc space-y-2 pl-6">
                    {block.items.map((item, itemIdx) => (
                      <li key={`${idx}-${itemIdx}`}>{item}</li>
                    ))}
                  </ul>
                );
              }

              if (block.kind === 'subsection') {
                return (
                  <h3
                    key={`h3-${idx}`}
                    className="pt-2 font-display text-[18px] leading-snug tracking-[-0.01em] text-on-surface"
                  >
                    {block.text}
                  </h3>
                );
              }

              return (
                <p key={`p-${idx}`} className="text-on-surface-variant">
                  {block.text}
                </p>
              );
            })}
          </div>

          <div className="mt-10 border-t border-white/10 pt-6">
            <Link to="/" className="font-mono text-[11px] tracking-[0.16em] uppercase text-primary hover:text-[#ffcfbf]">
              Back to Home
            </Link>
          </div>
        </article>

        <aside className="hidden lg:block">
          <div className="sticky top-24 rounded-xl border border-white/10 bg-surface-low/60 p-5">
            <h3 className="font-mono text-[10px] tracking-[0.18em] uppercase text-on-surface-muted">On This Page</h3>
            <nav className="mt-4 flex max-h-[70vh] flex-col gap-2 overflow-auto pr-2">
              {sections.map((section) => (
                <a
                  key={section.id}
                  href={`#${section.id}`}
                  className="text-sm leading-snug text-on-surface-variant transition-colors hover:text-primary"
                >
                  {section.text}
                </a>
              ))}
            </nav>
          </div>
        </aside>
      </main>
    </div>
  );
}
